import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Compass } from 'lucide-react';

const Navigation = ({ scrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const navigate = useNavigate();

  const primaryLinks = [
    { name: 'Services', href: '/#services' },
    { name: 'Work', href: '/#stories' },
    { name: 'About', href: '/#about' },
    { name: 'Contact', href: '/#contact' },
  ];


  const handleAnchorClick = (e, href) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    if (href.startsWith('/#')) {
      const anchor = href.slice(2);
      if (window.location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const el = document.getElementById(anchor);
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        const el = document.getElementById(anchor);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(href);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'py-2' : 'py-3'}`}
      style={{
        background: scrolled
          ? 'rgba(8, 13, 26, 0.92)'
          : 'linear-gradient(180deg, rgba(8,13,26,0.95) 0%, rgba(8,13,26,0) 100%)',
        backdropFilter: scrolled ? 'blur(20px) saturate(180%)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(0, 212, 255, 0.12)' : 'none',
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }} className="flex items-center space-x-3 group cursor-pointer">
            <div className="relative w-9 h-9">
              <img
                src="/logo.png"
                alt="IronGate Software"
                className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div className="absolute inset-0 bg-cyan-400/20 blur-lg group-hover:bg-cyan-400/30 transition-all pointer-events-none opacity-0 group-hover:opacity-100" />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-lg font-bold gradient-text tracking-tight leading-none">IronGate</h1>
              <p className="text-[9px] text-gray-500 tracking-[0.2em] uppercase">Software Ltd</p>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {primaryLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => handleAnchorClick(e, link.href)}
                className="px-3 py-2 text-sm text-gray-400 hover:text-white transition-all duration-200 rounded-lg hover:bg-white/5 font-medium"
              >
                {link.name}
              </a>
            ))}
          </div>

          {/* Right side actions */}
          <div className="hidden lg:flex items-center space-x-3">
            {/* QE Navigator pill — clearly visible */}
            <Link
              to="/navigator"
              className="flex items-center space-x-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 group"
              style={{
                background: 'linear-gradient(135deg, rgba(0,212,255,0.12) 0%, rgba(0,102,255,0.12) 100%)',
                border: '1px solid rgba(0,212,255,0.35)',
                color: '#00d4ff',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'linear-gradient(135deg, rgba(0,212,255,0.22) 0%, rgba(0,102,255,0.22) 100%)'}
              onMouseLeave={e => e.currentTarget.style.background = 'linear-gradient(135deg, rgba(0,212,255,0.12) 0%, rgba(0,102,255,0.12) 100%)'}
            >
              <Compass className="w-3.5 h-3.5" />
              <span>QE Navigator</span>
            </Link>

            {/* CTA */}
            <a
              href="/#contact"
              onClick={(e) => handleAnchorClick(e, '/#contact')}
              className="px-5 py-2.5 text-sm font-semibold text-white rounded-lg transition-all duration-200"
              style={{ background: 'linear-gradient(135deg, #00b8e6 0%, #0066ff 100%)' }}
            >
              Get in Touch
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2.5 rounded-lg hover:bg-white/10 transition-colors text-gray-300"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ${mobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}
        style={{
          background: 'rgba(8, 13, 26, 0.98)',
          backdropFilter: 'blur(20px)',
          borderTop: mobileMenuOpen ? '1px solid rgba(0, 212, 255, 0.1)' : 'none',
        }}
      >
        <div className="px-6 py-6 space-y-1">
          {primaryLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              onClick={(e) => handleAnchorClick(e, link.href)}
              className="block px-4 py-3 text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-all font-medium"
            >
              {link.name}
            </a>
          ))}
          <Link
            to="/navigator"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center space-x-2 px-4 py-3 rounded-lg font-semibold"
            style={{ color: '#00d4ff', background: 'rgba(0,212,255,0.08)' }}
          >
            <Compass className="w-4 h-4" />
            <span>QE Navigator</span>
          </Link>
          <div className="pt-3">
            <a
              href="/#contact"
              onClick={(e) => handleAnchorClick(e, '/#contact')}
              className="block w-full px-6 py-3 text-center text-white font-semibold rounded-lg"
              style={{ background: 'linear-gradient(135deg, #00b8e6 0%, #0066ff 100%)' }}
            >
              Get in Touch
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
